/**
 * Visitor country for the US functional default (item 100).
 *
 * - Asked of our own `visitor-geo` edge function, never a third party from
 *   the browser.
 * - Cached for this tab only (sessionStorage `tango_geo`, country code only).
 * - Any failure or timeout resolves to null, which means "not US" (opt-in).
 * - A `?geo=US|GB|none` override exists for testing, honoured ONLY in dev or
 *   on the Lovable preview host — never on tango-networks.com or the
 *   published lovable.app address.
 */

export const GEO_CACHE_KEY = "tango_geo";
const GEO_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/visitor-geo`;
const CLIENT_TIMEOUT_MS = 2500;

export function geoOverrideAllowed(hostname: string, isDev: boolean): boolean {
  if (isDev) return true;
  const h = (hostname || "").toLowerCase();
  if (h === "localhost" || h === "127.0.0.1") return true;
  // Lovable preview hosts only. The published site (tango-networks.lovable.app)
  // and the custom domains are deliberately excluded.
  return h.startsWith("id-preview--") && h.endsWith(".lovable.app")
    || h.endsWith(".lovableproject.com");
}

/** Returns undefined when there is no (permitted) override. */
export function readGeoOverride(
  search: string,
  hostname: string,
  isDev: boolean,
): string | null | undefined {
  if (!geoOverrideAllowed(hostname, isDev)) return undefined;
  const v = new URLSearchParams(search).get("geo");
  if (v === null) return undefined;
  if (v.toLowerCase() === "none") return null;
  return /^[A-Za-z]{2}$/.test(v) ? v.toUpperCase() : null;
}

export function normaliseCountry(v: unknown): string | null {
  return typeof v === "string" && /^[A-Za-z]{2}$/.test(v) ? v.toUpperCase() : null;
}

export async function resolveVisitorCountry(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  const override = readGeoOverride(window.location.search, window.location.hostname, !!import.meta.env.DEV);
  if (override !== undefined) return override;

  try {
    const cached = sessionStorage.getItem(GEO_CACHE_KEY);
    if (cached !== null) return cached === "" ? null : normaliseCountry(cached);
  } catch { /* ignore */ }

  let country: string | null = null;
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), CLIENT_TIMEOUT_MS);
    const res = await fetch(GEO_URL, {
      method: "GET",
      signal: ctrl.signal,
      headers: { apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
    });
    clearTimeout(t);
    if (res.ok) {
      const j = await res.json();
      country = normaliseCountry(j?.country);
    }
  } catch { country = null; }

  try { sessionStorage.setItem(GEO_CACHE_KEY, country ?? ""); } catch { /* ignore */ }
  return country;
}

export function globalPrivacyControlOn(): boolean {
  try {
    return typeof navigator !== "undefined" && (navigator as any).globalPrivacyControl === true;
  } catch {
    return false;
  }
}
